import React, { useState, useImperativeHandle, forwardRef } from "react";
import MultilingualOptionEditor from "../../../../../components/admin/MultilingualOptionEditor/MultilingualOptionEditor";
import { useLanguage } from "../../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../../translations";
import { createMultilingualDisplayVariant } from "../../../../../utils/productHelpers";

const VariantsTab = forwardRef(({
    product,
    setProduct,
    getTotalVariantQuantity,
    shouldValidateQuantities,
    getQuantityValidation,
    autoDistributeQuantity,
    activeTab
}, ref) => {
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);

    // Option management states
    const [showVariantGenerator, setShowVariantGenerator] = useState(false);

    // Image management states - NOW PER OPTION VALUE instead of per variant
    const [selectedOptionForImages, setSelectedOptionForImages] = useState(null);
    const [optionValueImageFiles, setOptionValueImageFiles] = useState({}); // { "optionId:valueKey": [files] }
    const [optionValuePreviewImages, setOptionValuePreviewImages] = useState({}); // { "optionId:valueKey": [urls] }

    // Expose option value image files to parent component
    useImperativeHandle(ref, () => ({
        getOptionValueImageFiles: () => optionValueImageFiles,
        clearOptionValueImageFiles: () => {
            setOptionValueImageFiles({});
            // Cleanup preview URLs
            Object.values(optionValuePreviewImages).forEach(imageArray => {
                imageArray.forEach(url => URL.revokeObjectURL(url));
            });
            setOptionValuePreviewImages({});
        },
        // Keep backward compatibility during transition
        getVariantImageFiles: () => ({}),
        clearVariantImageFiles: () => { }
    }));

    const { availableLanguages } = useLanguage();

    //Helper function to create display variants from variant data with multilingual support
    const createDisplayVariants = (variants, options) => {
        return variants.map(variant => {
            // Create multilingual display variants
            return createMultilingualDisplayVariant(variant.id, { options }, variant);
        });
    };

    // Helper to create a key for option value images
    const getOptionValueKey = (optionId, value) => `${optionId}:${value}`;

    // Image handling functions for OPTION VALUES
    const handleOptionValueImageUpload = (optionId, value, files) => {
        const fileArray = Array.from(files);
        const key = getOptionValueKey(optionId, value);

        // Update selected files for this option value
        setOptionValueImageFiles(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), ...fileArray]
        }));

        // Create preview URLs
        const newPreviews = fileArray.map(file => URL.createObjectURL(file));
        setOptionValuePreviewImages(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), ...newPreviews]
        }));
    };

    const removeOptionValueImage = (optionId, value, imageIndex, isExisting = false) => {
        const key = getOptionValueKey(optionId, value);

        if (isExisting) {
            // Remove from existing images in option.valueImages
            const updatedOptions = product.options.map(option => {
                if (option.id === optionId) {
                    const updatedValueImages = { ...(option.valueImages || {}) };
                    if (updatedValueImages[value]) {
                        const updatedImages = [...updatedValueImages[value]];
                        updatedImages.splice(imageIndex, 1);
                        if (updatedImages.length === 0) {
                            delete updatedValueImages[value];
                        } else {
                            updatedValueImages[value] = updatedImages;
                        }
                    }
                    return { ...option, valueImages: updatedValueImages };
                }
                return option;
            });

            setProduct({ ...product, options: updatedOptions });
        } else {
            // Remove from preview/files
            setOptionValueImageFiles(prev => {
                const updated = { ...prev };
                if (updated[key]) {
                    updated[key].splice(imageIndex, 1);
                    if (updated[key].length === 0) {
                        delete updated[key];
                    }
                }
                return updated;
            });

            setOptionValuePreviewImages(prev => {
                const updated = { ...prev };
                if (updated[key]) {
                    // Revoke URL to prevent memory leaks
                    URL.revokeObjectURL(updated[key][imageIndex]);
                    updated[key].splice(imageIndex, 1);
                    if (updated[key].length === 0) {
                        delete updated[key];
                    }
                }
                return updated;
            });
        }
    };

    // Updated option management functions for multilingual support
    const handleAddOption = () => {
        const newOption = {
            id: Date.now().toString(),
            name: { en: '', ar: '' }, // Start with empty multilingual object
            values: [],
            valueImages: {} // NEW: Store images per value
        };

        const updatedProduct = {
            ...product,
            options: [...product.options, newOption]
        };

        setProduct(updatedProduct);
    };

    const handleOptionUpdate = (updatedOption) => {
        const updatedOptions = product.options.map(option =>
            option.id === updatedOption.id ? updatedOption : option
        );

        const updatedProduct = {
            ...product,
            options: updatedOptions
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        }

        setProduct(updatedProduct);
    };

    const handleAddOptionValue = (optionId, value) => {
        const updatedOptions = product.options.map(option => {
            if (option.id === optionId) {
                return {
                    ...option,
                    values: [...option.values, value]
                };
            }
            return option;
        });

        const updatedProduct = {
            ...product,
            options: updatedOptions
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        }

        setProduct(updatedProduct);
    };

    const handleRemoveOptionValue = (optionId, valueToRemove) => {
        const updatedOptions = product.options.map(option => {
            if (option.id === optionId) {
                return {
                    ...option,
                    values: option.values.filter(v => v !== valueToRemove)
                };
            }
            return option;
        });

        const updatedProduct = {
            ...product,
            options: updatedOptions
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        }

        setProduct(updatedProduct);
    };

    const handleRemoveOption = (optionId) => {
        const updatedOptions = product.options.filter(opt => opt.id !== optionId);

        const updatedProduct = {
            ...product,
            options: updatedOptions
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        }

        setProduct(updatedProduct);
    };

    // Generate variants
    const generateVariants = () => {
        if (product.options.length === 0) return;

        const getCombinations = (options, current = {}, index = 0, results = []) => {
            if (index === options.length) {
                results.push({ ...current });
                return;
            }

            const option = options[index];
            // Handle both string and multilingual option names
            const optionKey = typeof option.name === 'string'
                ? option.name.toLowerCase()
                : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

            for (const value of option.values) {
                current[optionKey] = value;
                getCombinations(options, current, index + 1, results);
            }

            return results;
        };

        const optionCombinations = getCombinations(product.options);
        const basePrice = product.price || 0;

        // Handle baseSku - could be string or multilingual object
        let baseSku = product.sku || product.name || 'PROD';
        if (typeof baseSku === 'object') {
            // Extract English name for SKU if it's multilingual
            baseSku = baseSku.en || Object.values(baseSku)[0] || 'PROD';
        }
        baseSku = String(baseSku); // Ensure it's a string

        // Determine if product has infinite quantity
        const hasInfiniteQuantity = parseInt(product.quantity) === -1;
        const baseQuantity = hasInfiniteQuantity ? -1 : 0;

        const existingVariantsMap = {};
        product.variants.forEach(variant => {
            const key = Object.entries(variant)
                .filter(([k]) => !['id', 'price', 'sku', 'quantity', 'comparePrice', 'images'].includes(k))
                .map(([k, v]) => `${k}:${v}`)
                .sort()
                .join('|');

            existingVariantsMap[key] = variant;
        });

        const newVariants = optionCombinations.map(combination => {
            const key = Object.entries(combination)
                .map(([k, v]) => `${k}:${v}`)
                .sort()
                .join('|');

            if (existingVariantsMap[key]) {
                return existingVariantsMap[key];
            }

            // Create better variant SKU
            const variantSkuParts = [];

            // Add base SKU or product name
            const baseSlug = baseSku.toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            variantSkuParts.push(baseSlug);

            // Add option values to SKU
            Object.entries(combination).forEach(([key, value]) => {
                let cleanValue = value;

                // Handle multilingual values uniformly (no special color handling)
                try {
                    const valueData = JSON.parse(value);
                    if (typeof valueData === 'object' && valueData !== null) {
                        // Use English value for SKU consistency
                        cleanValue = valueData.en || Object.values(valueData)[0] || value;
                    }
                } catch {
                    cleanValue = value;
                }

                // Create clean slug part
                const slugPart = cleanValue.toString().toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '');

                if (slugPart) {
                    variantSkuParts.push(slugPart);
                }
            });

            const variantSku = variantSkuParts.join('-').toUpperCase();

            return {
                id: Date.now() + Math.random().toString(36).substr(2, 5),
                ...combination,
                price: basePrice,
                sku: variantSku,
                quantity: baseQuantity, // Use -1 if product has infinite quantity, otherwise 0
                images: []
            };
        });

        const displayVariants = createDisplayVariants(newVariants, product.options);

        setProduct({
            ...product,
            variants: newVariants,
            displayVariants: displayVariants
        });

        setShowVariantGenerator(false);
    };

    const handleVariantChange = (variantId, field, value) => {
        const updatedVariants = product.variants.map(variant => {
            if (variant.id === variantId) {
                return {
                    ...variant,
                    [field]: value
                };
            }
            return variant;
        });

        const updatedProduct = {
            ...product,
            variants: updatedVariants
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        }

        setProduct(updatedProduct);
    };

    const handleRemoveVariant = (variantId) => {
        const updatedVariants = product.variants.filter(v => v.id !== variantId);

        const updatedProduct = {
            ...product,
            variants: updatedVariants
        };

        if (updatedProduct.variants.length > 0) {
            updatedProduct.displayVariants = createDisplayVariants(updatedProduct.variants, updatedProduct.options);
        } else {
            updatedProduct.displayVariants = [];
        }

        setProduct(updatedProduct);
    };

    // Helper function to get variant display name (simplified, no color-specific logic)
    const getVariantDisplayName = (variant) => {
        const parts = [];
        product.options.forEach(option => {
            // Handle multilingual option names
            const optionKey = typeof option.name === 'string'
                ? option.name.toLowerCase()
                : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

            const optionDisplayName = typeof option.name === 'string'
                ? option.name
                : (option.name?.en || Object.values(option.name || {})[0] || 'Option');

            const value = variant[optionKey];
            if (value) {
                let displayValue = value;

                // Handle multilingual values uniformly
                try {
                    const valueData = JSON.parse(value);
                    if (typeof valueData === 'object' && valueData !== null) {
                        displayValue = valueData.en || Object.values(valueData)[0] || value;
                    }
                } catch {
                    displayValue = value;
                }

                parts.push(`${optionDisplayName}: ${displayValue}`);
            }
        });
        return parts.join(' • ');
    };

    // Helper to get display text for an option value
    const getValueDisplayText = (value) => {
        try {
            const parsed = JSON.parse(value);
            if (typeof parsed === 'object' && parsed !== null) {
                return parsed.en || Object.values(parsed)[0] || value;
            }
            return value;
        } catch {
            return value;
        }
    };

    // Render image management for a specific option's values
    const renderOptionValueImageManagement = (option) => {
        const optionName = typeof option.name === 'string'
            ? option.name
            : (option.name?.en || Object.values(option.name || {})[0] || 'Option');

        return (
            <div className="option-value-image-management">
                <h4>Images for {optionName}</h4>
                <p className="option-image-help">
                    Upload images for each {optionName.toLowerCase()} option. These images will be shown when a customer selects that option.
                </p>

                {option.values.map((value, valueIndex) => {
                    const key = getOptionValueKey(option.id, value);
                    const existingImages = option.valueImages?.[value] || [];
                    const previewImagesForValue = optionValuePreviewImages[key] || [];
                    const allImages = [...existingImages, ...previewImagesForValue];
                    const displayValue = getValueDisplayText(value);

                    return (
                        <div key={valueIndex} className="option-value-image-section">
                            <div className="option-value-image-header">
                                <span className="option-value-name">{displayValue}</span>
                                <span className="option-value-image-count">
                                    {allImages.length} image{allImages.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="option-value-image-upload">
                                <label htmlFor={`option-value-images-${key}`} className="image-upload-label small">
                                    <span>+ Add Images</span>
                                    <input
                                        type="file"
                                        id={`option-value-images-${key}`}
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleOptionValueImageUpload(option.id, value, e.target.files)}
                                        className="image-upload-input"
                                    />
                                </label>
                            </div>

                            {allImages.length > 0 && (
                                <div className="variant-images-grid">
                                    {/* Existing Images */}
                                    {existingImages.map((image, index) => (
                                        <div key={`existing-${index}`} className="variant-image-item">
                                            <img src={image} alt={`${displayValue} ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-image-btn"
                                                onClick={() => removeOptionValueImage(option.id, value, index, true)}
                                                title="Delete image"
                                            >
                                                ✕
                                            </button>

                                        </div>
                                    ))}

                                    {/* Preview Images */}
                                    {previewImagesForValue.map((imageUrl, index) => (
                                        <div key={`preview-${index}`} className="variant-image-item">
                                            <img src={imageUrl} alt={`New ${displayValue} ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-image-btn"
                                                onClick={() => removeOptionValueImage(option.id, value, index, false)}
                                                title="Remove image"
                                            >
                                                ✕
                                            </button>

                                        </div>
                                    ))}
                                </div>
                            )}

                            {allImages.length === 0 && (
                                <p className="no-images-message small">
                                    No images for "{displayValue}". Base product images will be used.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`editor-tab-content ${activeTab === "variants" ? "active" : ""}`}>
            <div className="editor-form-section">
                <h2>{t('productOptions')} & {t('variants')}</h2>
                <p className="editor-form-help">Define options like Size, Color, Material, etc. Then generate variants for all combinations.</p>

                {/* Quantity Validation Alert */}
                {shouldValidateQuantities() && !getQuantityValidation().isValid && (
                    <div className="quantity-validation-alert">
                        <div className="alert-content">
                            <span className="alert-icon">⚠️</span>
                            <div className="alert-message">
                                <strong>Quantity Mismatch:</strong> {getQuantityValidation().message}
                            </div>
                            <button
                                type="button"
                                className="alert-action-btn"
                                onClick={autoDistributeQuantity}
                            >
                                Auto-fix
                            </button>
                        </div>
                    </div>
                )}

                {/* Options Management Section */}
                <div className="options-container">
                    <h3>Options</h3>

                    {product.options.length > 0 ? (
                        <div className="options-list">
                            {product.options.map(option => (
                                <MultilingualOptionEditor
                                    key={option.id}
                                    option={option}
                                    onOptionUpdate={handleOptionUpdate}
                                    onOptionRemove={handleRemoveOption}
                                    onValueAdd={handleAddOptionValue}
                                    onValueRemove={handleRemoveOptionValue}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="no-options-message">
                            No options defined yet. Add options like Size, Color, etc.
                        </div>
                    )}

                    {/* Add New Option Button */}
                    <div className="add-option-form">
                        <button
                            type="button"
                            className="add-option-btn"
                            onClick={handleAddOption}
                        >
                            + Add New Option
                        </button>
                    </div>

                    {/* Generate Variants Section */}
                    {product.options.length > 0 && (
                        <div className="generate-variants-container">
                            <button
                                type="button"
                                className="generate-variants-btn"
                                onClick={() => setShowVariantGenerator(true)}
                            >
                                Generate Variants
                            </button>
                            <p className="generate-help">
                                This will create all possible combinations of your options.
                            </p>
                        </div>
                    )}

                    {/* Generate Variants Modal */}
                    {showVariantGenerator && (
                        <div className="generate-variants-modal">
                            <div className="modal-content">
                                <h3>Generate Product Variants</h3>
                                <p>
                                    This will create {product.options.reduce((acc, opt) => acc * opt.values.length, 1)} variants
                                    based on all combinations of your options:
                                </p>
                                <ul>
                                    {product.options.map(opt => {
                                        // Handle multilingual option names
                                        const optionName = typeof opt.name === 'string'
                                            ? opt.name
                                            : (opt.name?.en || Object.values(opt.name || {})[0] || 'Option');

                                        // Handle multilingual option values for display
                                        const displayValues = opt.values.map(value => {
                                            try {
                                                const parsedValue = JSON.parse(value);

                                                // Handle multilingual values uniformly
                                                if (typeof parsedValue === 'object' && parsedValue !== null) {
                                                    return parsedValue.en || Object.values(parsedValue)[0] || value;
                                                }

                                                return value;
                                            } catch {
                                                // If parsing fails, return original value
                                                return value;
                                            }
                                        });

                                        return (
                                            <li key={opt.id}>
                                                <strong>{optionName}:</strong> {displayValues.join(', ')}
                                            </li>
                                        );
                                    })}
                                </ul>
                                <p>
                                    {product.variants.length > 0
                                        ? "Existing variant data (price, inventory, images, etc.) will be preserved where possible."
                                        : "Each variant will start with the base product's price and SKU."}
                                </p>
                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="cancel-generate-btn"
                                        onClick={() => setShowVariantGenerator(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="confirm-generate-btn"
                                        onClick={generateVariants}
                                    >
                                        Generate Variants
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Variants Management Section */}
                {product.variants.length > 0 && (
                    <div className="variants-container">
                        <div className="variants-header">
                            <h3>Product Variants</h3>
                            {shouldValidateQuantities() && (
                                <div className="variants-quantity-summary">
                                    <span className={`quantity-status ${getQuantityValidation().isValid ? 'valid' : 'invalid'}`}>
                                        Total: {getTotalVariantQuantity()} / {product.quantity || 0}
                                        {getQuantityValidation().isValid ? ' ✓' : ' ⚠️'}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="variants-table-container">
                            <table className="variants-table">
                                <thead>
                                    <tr>
                                        {product.options.map(option => {
                                            // Display option name - handle both string and multilingual format
                                            const optionName = typeof option.name === 'string'
                                                ? option.name
                                                : (option.name?.en || Object.values(option.name || {})[0] || 'Option');

                                            return (
                                                <th key={option.id}>{optionName}</th>
                                            );
                                        })}
                                        <th>Price</th>
                                        <th>SKU</th>
                                        <th>Quantity</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product.variants.map(variant => (
                                        <tr key={variant.id}>
                                            {product.options.map(option => {
                                                // Get option key for lookup
                                                const optionKey = typeof option.name === 'string'
                                                    ? option.name.toLowerCase()
                                                    : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

                                                let displayValue = variant[optionKey] || "—";

                                                // Handle multilingual values uniformly
                                                if (displayValue !== "—") {
                                                    try {
                                                        const valueData = JSON.parse(displayValue);
                                                        if (typeof valueData === 'object' && valueData !== null) {
                                                            displayValue = valueData.en || Object.values(valueData)[0] || displayValue;
                                                        }
                                                    } catch {
                                                        // Keep original value if parsing fails
                                                    }
                                                }

                                                return (
                                                    <td key={`${variant.id}-${optionKey}`}>
                                                        {displayValue}
                                                    </td>
                                                );
                                            })}
                                            <td>
                                                <input
                                                    type="text"
                                                    value={variant.price || ""}
                                                    onChange={e => handleVariantChange(variant.id, 'price', e.target.value)}
                                                    className="variant-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={variant.sku || ""}
                                                    onChange={e => handleVariantChange(variant.id, 'sku', e.target.value)}
                                                    className="variant-input"
                                                />
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={parseInt(variant.quantity) === -1}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleVariantChange(variant.id, 'quantity', -1);
                                                                } else {
                                                                    handleVariantChange(variant.id, 'quantity', 0);
                                                                }
                                                            }}
                                                            style={{ width: '14px', height: '14px' }}
                                                        />
                                                        <span>∞ Unlimited</span>
                                                    </label>
                                                    {parseInt(variant.quantity) !== -1 && (
                                                        <input
                                                            type="number"
                                                            value={variant.quantity || "0"}
                                                            onChange={e => handleVariantChange(variant.id, 'quantity', e.target.value)}
                                                            min="0"
                                                            className="variant-input"
                                                        />
                                                    )}
                                                    {parseInt(variant.quantity) === -1 && (
                                                        <div style={{
                                                            padding: '5px',
                                                            background: '#e8f5e9',
                                                            borderRadius: '4px',
                                                            color: '#2e7d32',
                                                            fontSize: '12px',
                                                            textAlign: 'center'
                                                        }}>
                                                            ∞
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="variant-remove-btn"
                                                    onClick={() => handleRemoveVariant(variant.id)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Option Value Images Section - NEW efficient image management */}
                {product.options.length > 0 && product.options.some(opt => opt.values.length > 0) && (
                    <div className="option-images-section">
                        <div className="option-images-header">
                            <h3>Option Images</h3>
                            <p className="option-images-description">
                                Upload images for each option value. For example, add images for each color.

                            </p>
                        </div>

                        <div className="option-images-tabs">
                            {product.options.filter(opt => opt.values.length > 0).map(option => {
                                const optionName = typeof option.name === 'string'
                                    ? option.name
                                    : (option.name?.en || Object.values(option.name || {})[0] || 'Option');

                                // Count total images for this option
                                const totalImages = option.values.reduce((count, value) => {
                                    const key = getOptionValueKey(option.id, value);
                                    const existing = option.valueImages?.[value]?.length || 0;
                                    const pending = optionValuePreviewImages[key]?.length || 0;
                                    return count + existing + pending;
                                }, 0);

                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`option-image-tab ${selectedOptionForImages === option.id ? 'active' : ''}`}
                                        onClick={() => setSelectedOptionForImages(
                                            selectedOptionForImages === option.id ? null : option.id
                                        )}
                                    >
                                        {optionName}
                                        <span className="option-image-count">{totalImages}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Option Value Image Management Panel */}
                        {selectedOptionForImages && (
                            <div className="variant-image-management-container">
                                {product.options
                                    .filter(option => option.id === selectedOptionForImages)
                                    .map(option => (
                                        <div key={option.id}>
                                            {renderOptionValueImageManagement(option)}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

export default VariantsTab;
